import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SubscriptionCheckout = () => {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSubscriptionPackages();
    checkReturnFromStripe();
  }, []);

  const loadSubscriptionPackages = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/subscriptions/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Failed to load subscription packages:', error);
      setError('Kunne ikke laste abonnementspakker');
    } finally {
      setLoading(false);
    }
  };

  const checkReturnFromStripe = () => {
    // Check if we're returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setPaymentStatus('checking');
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setError('Betalingsstatusen tok for lang tid å sjekke. Vennligst kontakt support.');
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(
        `${API_BASE}/api/subscriptions/status/${sessionId}`, 
        { headers }
      );

      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        setError('Betalingsøkten utløp. Vennligst prøv igjen.');
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setError('Feil ved sjekking av betalingsstatus. Vennligst prøv igjen.');
    }
  };

  const initiatePayment = async (subscriptionType) => {
    if (!localStorage.getItem('token')) {
      setError('Du må være logget inn for å bestille abonnement');
      return;
    }

    setProcessingPayment(true);
    setError(null);

    try {
      const headers = { 
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      };

      const response = await axios.post(
        `${API_BASE}/api/subscriptions/checkout`,
        { subscription_type: subscriptionType },
        { headers }
      );

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('Ingen checkout URL mottatt');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);
      setError(error.response?.data?.detail || 'Kunne ikke starte betalingsprosess');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  // Payment status messages
  if (paymentStatus === 'success') {
    return (
      <div className="max-w-md mx-auto bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-green-900 mb-2">Betaling fullført!</h2>
        <p className="text-green-800">
          Ditt abonnement er nå aktivt. Du kan begynne å bruke alle funksjonene umiddelbart.
        </p>
      </div>
    );
  }

  if (paymentStatus === 'checking') {
    return (
      <div className="max-w-md mx-auto bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <Loader className="h-12 w-12 animate-spin text-blue-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-blue-900 mb-2">Sjekker betalingsstatus...</h2>
        <p className="text-blue-800">Vennligst vent mens vi bekrefter din betaling.</p>
      </div>
    );
  }

  if (paymentStatus === 'error' || paymentStatus === 'expired' || paymentStatus === 'timeout') {
    return (
      <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-red-900 mb-2">Betalingsfeil</h2>
        <p className="text-red-800 mb-4">{error}</p>
        <button
          onClick={() => {
            setPaymentStatus(null);
            setError(null);
            window.history.replaceState({}, document.title, window.location.pathname);
          }}
          className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Velg ditt abonnement</h1>
        <p className="text-lg text-gray-600">
          Start din digitale treningslogg i dag med sikker betaling via Stripe
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Start Package */}
        {packages.start && (
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{packages.start.name}</h3>
              <p className="text-gray-600 mt-2">{packages.start.description}</p>
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">
                  {packages.start.price}
                </span>
                <span className="text-lg text-gray-600"> {packages.start.currency}/mnd</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Inntil {packages.start.max_members} medlemmer
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              {packages.start.features.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => initiatePayment('start')}
              disabled={processingPayment}
              className="w-full bg-yellow-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingPayment ? (
                <span className="flex items-center justify-center">
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  Behandler...
                </span>
              ) : (
                'BESTILL START PAKKE'
              )}
            </button>
          </div>
        )}

        {/* Unlimited Package */}
        {packages.unlimited && (
          <div className="bg-white p-8 rounded-lg shadow-lg border-2 border-yellow-500 relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-yellow-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                Mest populær
              </span>
            </div>

            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">{packages.unlimited.name}</h3>
              <p className="text-gray-600 mt-2">{packages.unlimited.description}</p>
              <div className="mt-4">
                <span className="text-4xl font-bold text-gray-900">
                  {packages.unlimited.price}
                </span>
                <span className="text-lg text-gray-600"> {packages.unlimited.currency}/mnd</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {packages.unlimited.max_members ? `Inntil ${packages.unlimited.max_members} medlemmer` : 'Ubegrenset medlemmer'}
              </p>
            </div>

            <ul className="space-y-3 mb-8">
              {packages.unlimited.features.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => initiatePayment('unlimited')}
              disabled={processingPayment}
              className="w-full bg-yellow-500 text-white py-3 px-6 rounded-lg font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {processingPayment ? (
                <span className="flex items-center justify-center">
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  Behandler...
                </span>
              ) : (
                'BESTILL UBEGRENSET PAKKE'
              )}
            </button>
          </div>
        )}
      </div>

      <div className="text-center mt-8 max-w-3xl mx-auto">
        <div className="bg-blue-50 p-4 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">Sikker betaling med Stripe</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• SSL-kryptert og PCI-godkjent betalingsløsning</li>
            <li>• Støtter alle norske betalingskort</li>
            <li>• Kan avbestilles når som helst</li>
            <li>• Automatisk fakturering hver måned</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckout;